import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import {
  Package, Plus, ShoppingCart, RotateCcw, BarChart3,
  Loader, AlertCircle, ChevronDown, ChevronUp, Trash2,
  X, Download, FileText, Settings, Bell, CheckCircle, XCircle,
  ClipboardList,
} from 'lucide-react';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const MONTH_NAMES = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December',
];

const emptyPurchaseRow  = () => ({ item: '', quantity: 1, unit_price: '' });
const emptyIssuanceRow  = () => ({ item: '', quantity: 1, serial_or_mac: '' });
const emptyReturnRow    = () => ({ item: '', quantity: 1 });
const emptyRequestRow   = () => ({ item: '', quantity: 1, customer: '', customerSearch: '' });

const fieldCls = 'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500';
const lblCls   = 'block text-xs font-medium text-gray-600 mb-1';

// ── Shared Modal wrapper ──────────────────────────────────────────────────────
const Modal = ({ title, onClose, children }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
    <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0">
        <h3 className="font-semibold text-gray-900">{title}</h3>
        <button onClick={onClose} className="p-1 rounded hover:bg-gray-100 text-gray-500"><X size={18} /></button>
      </div>
      <div className="overflow-y-auto flex-1 px-6 py-4">{children}</div>
    </div>
  </div>
);

// ── Dynamic rows table used in purchase/issuance/return modals ────────────────
const LineItemRows = ({ rows, stockItems, onUpdate, onAdd, onRemove, showMac = false, showPrice = false, availableOnly = false }) => (
  <div>
    <div className="space-y-2">
      {rows.map((row, i) => (
        <div key={i} className="flex gap-2 items-center flex-wrap">
          <select
            value={row.item}
            onChange={e => onUpdate(i, 'item', e.target.value)}
            className={`${fieldCls} flex-1 min-w-36`}
          >
            <option value="">— Select item —</option>
            {stockItems
              .filter(s => !availableOnly || s.current_stock > 0)
              .map(s => (
                <option key={s.id} value={s.id}>
                  {s.name} ({s.unit}){availableOnly ? ` — ${s.current_stock} available` : ''}
                </option>
              ))}
          </select>
          <input
            type="number" min="1" value={row.quantity}
            onChange={e => onUpdate(i, 'quantity', e.target.value)}
            className={`${fieldCls} w-20`} placeholder="Qty"
          />
          {showPrice && (
            <input
              type="number" min="0" step="0.01" value={row.unit_price}
              onChange={e => onUpdate(i, 'unit_price', e.target.value)}
              className={`${fieldCls} w-28`} placeholder="Price (opt)"
            />
          )}
          {showMac && (
            <input
              type="text" value={row.serial_or_mac}
              onChange={e => onUpdate(i, 'serial_or_mac', e.target.value)}
              className={`${fieldCls} w-40`} placeholder="MAC/Serial (opt)"
            />
          )}
          {rows.length > 1 && (
            <button onClick={() => onRemove(i)} className="p-1.5 text-red-400 hover:bg-red-50 rounded flex-shrink-0">
              <Trash2 size={14} />
            </button>
          )}
        </div>
      ))}
    </div>
    <button onClick={onAdd} className="mt-2 flex items-center gap-1 text-xs text-blue-600 hover:underline">
      <Plus size={12} /> Add row
    </button>
  </div>
);

// ── Expandable list row ────────────────────────────────────────────────────────
const HistoryRow = ({ title, subtitle, meta, badge, expanded, onToggle, children }) => (
  <div>
    <div
      className="flex items-center justify-between px-6 py-4 hover:bg-gray-50 cursor-pointer transition-colors"
      onClick={onToggle}
    >
      <div>
        <p className="font-medium text-gray-900 flex items-center gap-2">
          {title}
          {badge}
        </p>
        <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>
      </div>
      <div className="flex items-center gap-3">
        {meta && <p className="text-xs text-gray-400">{meta}</p>}
        {expanded ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />}
      </div>
    </div>
    {expanded && (
      <div className="px-6 pb-4 bg-gray-50 border-t border-gray-100">{children}</div>
    )}
  </div>
);

const StatusBadge = ({ status }) => {
  const map = {
    pending:  'bg-yellow-100 text-yellow-700',
    issued:   'bg-green-100 text-green-700',
    rejected: 'bg-red-100 text-red-700',
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${map[status] || 'bg-gray-100 text-gray-600'}`}>
      {status}
    </span>
  );
};

// ── Customer search input ─────────────────────────────────────────────────────
const CustomerSearchField = ({ form, setForm, customerResults, setCustomerResults, searchingCustomers, onSearch }) => (
  <div className="relative">
    <label className={lblCls}>Customer (optional — link for modem/warranty tracking)</label>
    {form.customer ? (
      <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg text-sm">
        <span className="flex-1 text-blue-800 font-medium">{form.customerSearch}</span>
        <button
          onClick={() => { setForm(f => ({ ...f, customer: '', customerSearch: '' })); setCustomerResults([]); }}
          className="text-blue-400 hover:text-red-500"
        ><X size={14} /></button>
      </div>
    ) : (
      <>
        <div className="relative">
          <input
            type="text"
            value={form.customerSearch}
            onChange={e => {
              const v = e.target.value;
              setForm(f => ({ ...f, customerSearch: v, customer: '' }));
              onSearch(v);
            }}
            className={fieldCls}
            placeholder="Type username or name to search..."
            autoComplete="off"
          />
          {searchingCustomers && <Loader size={14} className="animate-spin absolute right-3 top-2.5 text-gray-400" />}
        </div>
        {customerResults.length > 0 && (
          <div className="absolute z-10 mt-1 w-full border border-gray-200 rounded-lg bg-white shadow-lg max-h-48 overflow-y-auto">
            {customerResults.map(c => (
              <button key={c.id} className="w-full text-left px-3 py-2.5 text-sm hover:bg-blue-50 flex items-center justify-between gap-2"
                onClick={() => {
                  setForm(f => ({ ...f, customer: c.id, customerSearch: `${c.name} (${c.username})` }));
                  setCustomerResults([]);
                }}>
                <div>
                  <span className="font-medium text-gray-900">{c.name}</span>
                  <span className="text-xs text-gray-400 ml-2">{c.username}</span>
                </div>
                <span className="text-xs text-gray-300 flex-shrink-0">#{c.id}</span>
              </button>
            ))}
          </div>
        )}
      </>
    )}
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
const Stock = () => {
  const now = new Date();
  const today = now.toLocaleDateString('en-CA');

  const [userInfo, setUserInfo] = useState(null);
  const [tab, setTab] = useState('inventory');
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Reference data
  const [stockItems, setStockItems] = useState([]);
  const [employees, setEmployees] = useState([]);

  // Inventory (with stock annotations)
  const [inventory, setInventory] = useState([]);
  const [loadingInventory, setLoadingInventory] = useState(true);

  // Purchases
  const [purchases, setPurchases] = useState([]);
  const [loadingPurchases, setLoadingPurchases] = useState(false);
  const [expandedPurchase, setExpandedPurchase] = useState(null);

  // Issuances
  const [issuances, setIssuances] = useState([]);
  const [loadingIssuances, setLoadingIssuances] = useState(false);
  const [expandedIssuance, setExpandedIssuance] = useState(null);

  // Returns
  const [returns, setReturns] = useState([]);
  const [loadingReturns, setLoadingReturns] = useState(false);
  const [expandedReturn, setExpandedReturn] = useState(null);

  // Stock Requests
  const [requests, setRequests] = useState([]);
  const [loadingRequests, setLoadingRequests] = useState(false);
  const [expandedRequest, setExpandedRequest] = useState(null);

  // Report
  const [reportYear, setReportYear] = useState(now.getFullYear());
  const [reportMonth, setReportMonth] = useState(now.getMonth() + 1);
  const [report, setReport] = useState([]);
  const [loadingReport, setLoadingReport] = useState(false);

  // Modals
  const [showPurchaseModal, setShowPurchaseModal]   = useState(false);
  const [showIssuanceModal, setShowIssuanceModal]   = useState(false);
  const [showReturnModal, setShowReturnModal]       = useState(false);
  const [showAddItemModal, setShowAddItemModal]     = useState(false);
  const [editingItem, setEditingItem]               = useState(null);
  const [showRequestModal, setShowRequestModal]     = useState(false);
  const [approvingRequest, setApprovingRequest]     = useState(null); // request obj being approved
  const [approveSerial, setApproveSerial]           = useState('');
  const [rejectingRequest, setRejectingRequest]     = useState(null);
  const [rejectNote, setRejectNote]                 = useState('');

  // Form state
  const [purchaseForm, setPurchaseForm] = useState({ date: today, supplier: '', notes: '', rows: [emptyPurchaseRow()] });
  const [issuanceForm, setIssuanceForm] = useState({ date: today, issued_to: '', customer: '', customerSearch: '', notes: '', rows: [emptyIssuanceRow()] });
  const [returnForm, setReturnForm]     = useState({ date: today, returned_by: '', notes: '', rows: [emptyReturnRow()] });
  const [newItemForm, setNewItemForm]   = useState({ name: '', unit: 'pcs', low_threshold: 5, notes: '' });
  const [requestRows, setRequestRows]   = useState([emptyRequestRow()]);
  const [requestNotes, setRequestNotes] = useState('');
  const [activeCustomerRow, setActiveCustomerRow] = useState(null);

  // Customer search (shared between issuance + request modals)
  const [customerResults, setCustomerResults] = useState([]);
  const [searchingCustomers, setSearchingCustomers] = useState(false);
  const [customerSearchEmpty, setCustomerSearchEmpty] = useState(false);

  const isManager = userInfo?.is_stock_manager || userInfo?.is_superuser || userInfo?.is_manager;

  // ── Fetchers ──────────────────────────────────────────────────────────────
  const fetchInventory = useCallback(async () => {
    setLoadingInventory(true);
    try {
      const res = await api.get('/stock-items/inventory/');
      setInventory(res.data);
    } catch (e) { console.error(e); }
    finally { setLoadingInventory(false); }
  }, []);

  const fetchStockItems = useCallback(async () => {
    try {
      const res = await api.get('/stock-items/');
      setStockItems(res.data.results || res.data);
    } catch (e) { console.error(e); }
  }, []);

  const fetchRequests = useCallback(async () => {
    setLoadingRequests(true);
    try {
      const res = await api.get('/stock-requests/');
      setRequests(res.data.results || res.data);
    } catch (e) { console.error(e); }
    finally { setLoadingRequests(false); }
  }, []);

  useEffect(() => {
    api.get('/users/me/').then(r => setUserInfo(r.data)).catch(console.error);
    api.get('/employees/').then(r => setEmployees(r.data.results || r.data)).catch(console.error);
    fetchInventory();
    fetchStockItems();
  }, [fetchInventory, fetchStockItems]);

  const searchCustomers = useCallback(async (query) => {
    if (!query || query.length < 2) { setCustomerResults([]); setCustomerSearchEmpty(false); return; }
    setSearchingCustomers(true);
    setCustomerSearchEmpty(false);
    try {
      const res = await api.get('/customers/', { params: { search: query, page_size: 10 } });
      const results = res.data.results || res.data;
      setCustomerResults(results);
      setCustomerSearchEmpty(results.length === 0);
    } catch (e) { console.error(e); }
    finally { setSearchingCustomers(false); }
  }, []);

  useEffect(() => {
    if (tab === 'purchases' && purchases.length === 0) {
      setLoadingPurchases(true);
      api.get('/stock-purchases/').then(r => setPurchases(r.data.results || r.data)).catch(console.error).finally(() => setLoadingPurchases(false));
    }
    if (tab === 'issuances' && issuances.length === 0) {
      setLoadingIssuances(true);
      api.get('/stock-issuances/').then(r => setIssuances(r.data.results || r.data)).catch(console.error).finally(() => setLoadingIssuances(false));
    }
    if (tab === 'returns' && returns.length === 0) {
      setLoadingReturns(true);
      api.get('/stock-returns/').then(r => setReturns(r.data.results || r.data)).catch(console.error).finally(() => setLoadingReturns(false));
    }
    if (tab === 'requests') {
      fetchRequests();
    }
  }, [tab]);

  // ── Submit handlers ───────────────────────────────────────────────────────
  const openModal = (setter) => { setError(null); setter(true); };

  const submitPurchase = async () => {
    const items = purchaseForm.rows.filter(r => r.item && Number(r.quantity) > 0)
      .map(r => ({ item: Number(r.item), quantity: Number(r.quantity), unit_price: r.unit_price || null }));
    if (!purchaseForm.supplier || items.length === 0) return setError('Supplier and at least one item are required.');
    setSubmitting(true); setError(null);
    try {
      const res = await api.post('/stock-purchases/', { date: purchaseForm.date, supplier: purchaseForm.supplier, notes: purchaseForm.notes, items });
      setPurchases(p => [res.data, ...p]);
      setShowPurchaseModal(false);
      setPurchaseForm({ date: today, supplier: '', notes: '', rows: [emptyPurchaseRow()] });
      fetchInventory();
    } catch (e) { setError(e.response?.data?.detail || JSON.stringify(e.response?.data) || 'Failed to save.'); }
    finally { setSubmitting(false); }
  };

  const submitIssuance = async () => {
    const items = issuanceForm.rows.filter(r => r.item && Number(r.quantity) > 0)
      .map(r => ({ item: Number(r.item), quantity: Number(r.quantity), serial_or_mac: r.serial_or_mac || '' }));
    if (!issuanceForm.issued_to || items.length === 0) return setError('Employee and at least one item are required.');
    setSubmitting(true); setError(null);
    try {
      const res = await api.post('/stock-issuances/', {
        date: issuanceForm.date,
        issued_to: Number(issuanceForm.issued_to),
        customer: issuanceForm.customer ? Number(issuanceForm.customer) : null,
        notes: issuanceForm.notes,
        items,
      });
      setIssuances(p => [res.data, ...p]);
      setShowIssuanceModal(false);
      setIssuanceForm({ date: today, issued_to: '', customer: '', customerSearch: '', notes: '', rows: [emptyIssuanceRow()] });
      fetchInventory();
    } catch (e) { setError(e.response?.data?.detail || JSON.stringify(e.response?.data) || 'Failed to save.'); }
    finally { setSubmitting(false); }
  };

  const submitReturn = async () => {
    const items = returnForm.rows.filter(r => r.item && Number(r.quantity) > 0)
      .map(r => ({ item: Number(r.item), quantity: Number(r.quantity) }));
    if (!returnForm.returned_by || items.length === 0) return setError('Employee and at least one item are required.');
    setSubmitting(true); setError(null);
    try {
      const res = await api.post('/stock-returns/', { date: returnForm.date, returned_by: Number(returnForm.returned_by), notes: returnForm.notes, items });
      setReturns(p => [res.data, ...p]);
      setShowReturnModal(false);
      setReturnForm({ date: today, returned_by: '', notes: '', rows: [emptyReturnRow()] });
      fetchInventory();
    } catch (e) { setError(e.response?.data?.detail || JSON.stringify(e.response?.data) || 'Failed to save.'); }
    finally { setSubmitting(false); }
  };

  const submitNewItem = async () => {
    if (!newItemForm.name || !newItemForm.unit) return setError('Name and unit are required.');
    setSubmitting(true); setError(null);
    try {
      await api.post('/stock-items/', newItemForm);
      setShowAddItemModal(false);
      setNewItemForm({ name: '', unit: 'pcs', low_threshold: 5, notes: '' });
      fetchInventory();
      fetchStockItems();
    } catch (e) { setError(e.response?.data?.detail || JSON.stringify(e.response?.data) || 'Failed to save.'); }
    finally { setSubmitting(false); }
  };

  const openEditItem = (item) => {
    setEditingItem(item);
    setNewItemForm({ name: item.name, unit: item.unit, low_threshold: item.low_threshold, notes: item.notes || '' });
    setError(null);
  };

  const submitEditItem = async () => {
    if (!newItemForm.name || !newItemForm.unit) return setError('Name and unit are required.');
    setSubmitting(true); setError(null);
    try {
      await api.patch(`/stock-items/${editingItem.id}/`, newItemForm);
      setEditingItem(null);
      setNewItemForm({ name: '', unit: 'pcs', low_threshold: 5, notes: '' });
      fetchInventory();
      fetchStockItems();
    } catch (e) { setError(e.response?.data?.detail || JSON.stringify(e.response?.data) || 'Failed to save.'); }
    finally { setSubmitting(false); }
  };

  const submitRequest = async () => {
    const validRows = requestRows.filter(r => r.item && Number(r.quantity) > 0);
    if (validRows.length === 0) return setError('Select at least one item.');
    setSubmitting(true); setError(null);
    try {
      const results = await Promise.all(validRows.map(row =>
        api.post('/stock-requests/', {
          item: Number(row.item),
          quantity: Number(row.quantity),
          notes: requestNotes,
          customer: row.customer ? Number(row.customer) : null,
        })
      ));
      setRequests(r => [...results.map(r => r.data), ...r]);
      setShowRequestModal(false);
      setRequestRows([emptyRequestRow()]);
      setRequestNotes('');
      setCustomerResults([]);
      setActiveCustomerRow(null);
    } catch (e) { setError(e.response?.data?.detail || JSON.stringify(e.response?.data) || 'Failed to submit request.'); }
    finally { setSubmitting(false); }
  };

  const approveRequest = async () => {
    setSubmitting(true); setError(null);
    try {
      const res = await api.post(`/stock-requests/${approvingRequest.id}/approve/`, { serial_or_mac: approveSerial });
      setRequests(r => r.map(x => x.id === res.data.id ? res.data : x));
      setApprovingRequest(null);
      setApproveSerial('');
      fetchInventory();
      // Also refresh issuances list if it was loaded
      if (issuances.length > 0) {
        api.get('/stock-issuances/').then(r => setIssuances(r.data.results || r.data)).catch(console.error);
      }
    } catch (e) { setError(e.response?.data?.error || 'Failed to approve.'); }
    finally { setSubmitting(false); }
  };

  const rejectRequest = async () => {
    setSubmitting(true); setError(null);
    try {
      const res = await api.post(`/stock-requests/${rejectingRequest.id}/reject/`, { rejection_note: rejectNote });
      setRequests(r => r.map(x => x.id === res.data.id ? res.data : x));
      setRejectingRequest(null);
      setRejectNote('');
    } catch (e) { setError(e.response?.data?.error || 'Failed to reject.'); }
    finally { setSubmitting(false); }
  };

  // ── Report ────────────────────────────────────────────────────────────────
  const loadReport = useCallback(async (year, month) => {
    setLoadingReport(true);
    try {
      const res = await api.get('/stock-items/report/', { params: { year, month } });
      setReport(res.data);
    } catch (e) { console.error(e); }
    finally { setLoadingReport(false); }
  }, []);

  useEffect(() => {
    if (tab === 'report') loadReport(reportYear, reportMonth);
  }, [tab, reportYear, reportMonth]);

  const exportExcel = () => {
    const ws = XLSX.utils.json_to_sheet(report.map(r => ({
      Item: r.name, Unit: r.unit,
      Purchased: r.purchased, Issued: r.issued, Returned: r.returned, Remaining: r.remaining,
    })));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, `Stock ${MONTH_NAMES[reportMonth - 1]} ${reportYear}`);
    XLSX.writeFile(wb, `stock-report-${reportYear}-${String(reportMonth).padStart(2, '0')}.xlsx`);
  };

  const exportPDF = () => {
    const doc = new jsPDF();
    doc.setFontSize(14);
    doc.text(`Stock Report — ${MONTH_NAMES[reportMonth - 1]} ${reportYear}`, 14, 20);
    autoTable(doc, {
      startY: 28,
      head: [['Item', 'Unit', 'Purchased', 'Issued', 'Returned', 'Remaining']],
      body: report.map(r => [r.name, r.unit, r.purchased, r.issued, r.returned, r.remaining]),
      styles: { fontSize: 9 },
      headStyles: { fillColor: [37, 99, 235] },
      alternateRowStyles: { fillColor: [248, 250, 252] },
    });
    doc.save(`stock-report-${reportYear}-${String(reportMonth).padStart(2, '0')}.pdf`);
  };

  // ── Row helpers ───────────────────────────────────────────────────────────
  const pRow = (i, k, v) => setPurchaseForm(f => ({ ...f, rows: f.rows.map((r, idx) => idx === i ? { ...r, [k]: v } : r) }));
  const iRow = (i, k, v) => setIssuanceForm(f => ({ ...f, rows: f.rows.map((r, idx) => idx === i ? { ...r, [k]: v } : r) }));
  const rRow = (i, k, v) => setReturnForm(f =>   ({ ...f, rows: f.rows.map((r, idx) => idx === i ? { ...r, [k]: v } : r) }));

  // ── Shared helpers ────────────────────────────────────────────────────────
  const Spinner = () => <div className="p-10 text-center"><Loader className="animate-spin text-blue-500 mx-auto" size={28} /></div>;
  const Empty = ({ msg }) => <div className="p-10 text-center text-gray-400 text-sm">{msg}</div>;

  const itemsTable = (items, showMac = false) => (
    <table className="w-full text-sm mt-3">
      <thead>
        <tr className="text-left">
          <th className="pb-2 text-xs font-semibold text-gray-500 uppercase">Item</th>
          <th className="pb-2 text-xs font-semibold text-gray-500 uppercase text-right">Qty</th>
          {showMac && <th className="pb-2 text-xs font-semibold text-gray-500 uppercase pl-3">MAC / Serial</th>}
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-100">
        {items.map(line => (
          <tr key={line.id}>
            <td className="py-1.5 text-gray-800">{line.item_name} <span className="text-gray-400 text-xs">({line.item_unit})</span></td>
            <td className="py-1.5 text-right font-semibold">{line.quantity}</td>
            {showMac && <td className="py-1.5 pl-3 text-gray-500 text-xs">{line.serial_or_mac || '—'}</td>}
          </tr>
        ))}
      </tbody>
    </table>
  );

  const pendingCount = requests.filter(r => r.status === 'pending').length;

  // ── Tab definitions differ by role ────────────────────────────────────────
  const managerTabs = [
    { id: 'inventory', label: 'Inventory' },
    { id: 'purchases', label: 'Purchases' },
    { id: 'issuances', label: 'Issuances' },
    { id: 'returns', label: 'Returns' },
    { id: 'requests', label: 'Requests', badge: pendingCount },
    { id: 'report', label: 'Monthly Report' },
  ];
  const employeeTabs = [
    { id: 'inventory', label: 'Available Stock' },
    { id: 'requests', label: 'My Requests' },
  ];
  const tabs = isManager ? managerTabs : employeeTabs;

  return (
    <div className="p-4 sm:p-6 bg-gray-50 min-h-screen">

      {/* Header */}
      <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Package className="text-blue-600" size={24} /> Stock Management
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            {isManager ? 'Track purchases, issuances, returns and live inventory' : 'Request items from stock'}
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {!isManager && (
            <button onClick={() => { setError(null); setRequestRows([emptyRequestRow()]); setRequestNotes(''); setActiveCustomerRow(null); setShowRequestModal(true); }}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium">
              <Plus size={14} /> Request Items
            </button>
          )}
          {isManager && (
            <>
              <button onClick={() => openModal(setShowAddItemModal)}
                className="flex items-center gap-2 px-3 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm font-medium">
                <Settings size={14} /> Manage Items
              </button>
              <button onClick={() => openModal(setShowPurchaseModal)}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium">
                <ShoppingCart size={14} /> Log Purchase
              </button>
              <button onClick={() => openModal(setShowIssuanceModal)}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium">
                <Plus size={14} /> Issue Items
              </button>
              <button onClick={() => openModal(setShowReturnModal)}
                className="flex items-center gap-2 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 text-sm font-medium">
                <RotateCcw size={14} /> Log Return
              </button>
            </>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 bg-white rounded-xl p-1 border border-gray-100 shadow-sm overflow-x-auto">
        {tabs.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`flex-1 px-4 py-2 text-sm font-medium rounded-lg transition-colors whitespace-nowrap flex items-center justify-center gap-1.5 ${
              tab === t.id ? 'bg-blue-600 text-white shadow' : 'text-gray-600 hover:bg-gray-100'
            }`}>
            {t.label}
            {t.badge > 0 && (
              <span className={`inline-flex items-center justify-center w-5 h-5 text-xs rounded-full font-bold ${
                tab === t.id ? 'bg-white text-blue-600' : 'bg-red-500 text-white'
              }`}>{t.badge}</span>
            )}
          </button>
        ))}
      </div>

      {/* ── Inventory ── */}
      {tab === 'inventory' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          {loadingInventory ? <Spinner /> : (() => {
            const displayInventory = isManager ? inventory : inventory.filter(i => i.current_stock > 0);
            if (displayInventory.length === 0) return (
              <div className="p-10 text-center text-gray-400">
                <Package size={40} className="mx-auto mb-2 text-gray-200" />
                <p className="text-sm">{isManager ? 'No stock items yet.' : 'No items currently in stock.'}</p>
                {isManager && (
                  <button onClick={() => openModal(setShowAddItemModal)}
                    className="mt-3 px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700">
                    Add First Item
                  </button>
                )}
              </div>
            );
            return (
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Item</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Unit</th>
                  <th className="px-6 py-3 text-right text-xs font-semibold text-gray-500 uppercase">In Stock</th>
                  <th className="px-6 py-3 text-center text-xs font-semibold text-gray-500 uppercase">Status</th>
                  {isManager && <>
                    <th className="px-6 py-3 text-right text-xs font-semibold text-gray-500 uppercase hidden sm:table-cell">Purchased</th>
                    <th className="px-6 py-3 text-right text-xs font-semibold text-gray-500 uppercase hidden sm:table-cell">Issued</th>
                    <th className="px-6 py-3 text-right text-xs font-semibold text-gray-500 uppercase hidden sm:table-cell">Returned</th>
                    <th className="px-6 py-3 w-10"></th>
                  </>}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {displayInventory.map(item => (
                  <tr key={item.id} className="hover:bg-gray-50 transition-colors group">
                    <td className="px-6 py-3 font-medium text-gray-900">{item.name}</td>
                    <td className="px-6 py-3 text-gray-500">{item.unit}</td>
                    <td className="px-6 py-3 text-right font-bold text-lg text-gray-900">{item.current_stock}</td>
                    <td className="px-6 py-3 text-center">
                      {item.is_low
                        ? <span className="inline-flex items-center px-2 py-0.5 bg-red-100 text-red-700 rounded-full text-xs font-semibold">Low</span>
                        : <span className="inline-flex items-center px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-xs font-semibold">OK</span>
                      }
                    </td>
                    {isManager && <>
                      <td className="px-6 py-3 text-right text-gray-400 hidden sm:table-cell">{item.purchased}</td>
                      <td className="px-6 py-3 text-right text-gray-400 hidden sm:table-cell">{item.issued}</td>
                      <td className="px-6 py-3 text-right text-gray-400 hidden sm:table-cell">{item.returned}</td>
                      <td className="px-3 py-3 text-right">
                        <button
                          onClick={() => openEditItem(item)}
                          className="opacity-0 group-hover:opacity-100 p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-all"
                          title="Edit item"
                        >
                          <Settings size={14} />
                        </button>
                      </td>
                    </>}
                  </tr>
                ))}
              </tbody>
            </table>
            );
          })()}
        </div>
      )}

      {/* ── Purchases ── */}
      {tab === 'purchases' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
            <h2 className="font-semibold text-gray-800">Purchase History</h2>
            <button onClick={() => openModal(setShowPurchaseModal)}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium">
              <Plus size={14} /> New Purchase
            </button>
          </div>
          {loadingPurchases ? <Spinner /> : purchases.length === 0 ? <Empty msg="No purchases logged yet." /> : (
            <div className="divide-y divide-gray-100">
              {purchases.map(p => (
                <HistoryRow key={p.id}
                  title={p.supplier}
                  subtitle={`${p.date} · ${p.item_count} item type${p.item_count !== 1 ? 's' : ''}`}
                  meta={`by ${p.created_by_name || 'System'}`}
                  expanded={expandedPurchase === p.id}
                  onToggle={() => setExpandedPurchase(expandedPurchase === p.id ? null : p.id)}
                >
                  <table className="w-full text-sm mt-3">
                    <thead>
                      <tr className="text-left">
                        <th className="pb-2 text-xs font-semibold text-gray-500 uppercase">Item</th>
                        <th className="pb-2 text-xs font-semibold text-gray-500 uppercase text-right">Qty</th>
                        <th className="pb-2 text-xs font-semibold text-gray-500 uppercase text-right">Unit Price</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {p.items.map(line => (
                        <tr key={line.id}>
                          <td className="py-1.5 text-gray-800">{line.item_name} <span className="text-gray-400 text-xs">({line.item_unit})</span></td>
                          <td className="py-1.5 text-right font-semibold">{line.quantity}</td>
                          <td className="py-1.5 text-right text-gray-500 text-xs">
                            {line.unit_price ? `PKR ${Number(line.unit_price).toLocaleString()}` : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {p.notes && <p className="text-xs text-gray-400 mt-2">Notes: {p.notes}</p>}
                </HistoryRow>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Issuances ── */}
      {tab === 'issuances' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
            <h2 className="font-semibold text-gray-800">Issuance History</h2>
            <button onClick={() => openModal(setShowIssuanceModal)}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium">
              <Plus size={14} /> Issue Items
            </button>
          </div>
          {loadingIssuances ? <Spinner /> : issuances.length === 0 ? <Empty msg="No issuances yet." /> : (
            <div className="divide-y divide-gray-100">
              {issuances.map(is => (
                <HistoryRow key={is.id}
                  title={is.issued_to_name}
                  subtitle={`${is.date} · ${is.item_count} item type${is.item_count !== 1 ? 's' : ''}`}
                  meta={`by ${is.created_by_name || 'System'}`}
                  expanded={expandedIssuance === is.id}
                  onToggle={() => setExpandedIssuance(expandedIssuance === is.id ? null : is.id)}
                >
                  {(is.customer_name || is.customer_username) && (
                    <div className="mt-3 mb-2 flex items-center gap-2 text-xs text-blue-700 bg-blue-50 rounded-lg px-3 py-2">
                      <span className="font-semibold">Customer:</span>
                      <span>{is.customer_name}</span>
                      {is.customer_username && <span className="text-blue-400">({is.customer_username})</span>}
                    </div>
                  )}
                  {itemsTable(is.items, true)}
                  {is.notes && <p className="text-xs text-gray-400 mt-2">Notes: {is.notes}</p>}
                </HistoryRow>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Returns ── */}
      {tab === 'returns' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
            <h2 className="font-semibold text-gray-800">Return History</h2>
            <button onClick={() => openModal(setShowReturnModal)}
              className="flex items-center gap-2 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 text-sm font-medium">
              <RotateCcw size={14} /> Log Return
            </button>
          </div>
          {loadingReturns ? <Spinner /> : returns.length === 0 ? <Empty msg="No returns yet." /> : (
            <div className="divide-y divide-gray-100">
              {returns.map(ret => (
                <HistoryRow key={ret.id}
                  title={ret.returned_by_name}
                  subtitle={`${ret.date} · ${ret.item_count} item type${ret.item_count !== 1 ? 's' : ''}`}
                  meta={`by ${ret.created_by_name || 'System'}`}
                  expanded={expandedReturn === ret.id}
                  onToggle={() => setExpandedReturn(expandedReturn === ret.id ? null : ret.id)}
                >
                  {itemsTable(ret.items)}
                  {ret.notes && <p className="text-xs text-gray-400 mt-2">Notes: {ret.notes}</p>}
                </HistoryRow>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Requests ── */}
      {tab === 'requests' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
            <h2 className="font-semibold text-gray-800 flex items-center gap-2">
              <ClipboardList size={16} />
              {isManager ? 'Stock Requests' : 'My Requests'}
              {pendingCount > 0 && (
                <span className="bg-red-500 text-white text-xs font-bold px-1.5 py-0.5 rounded-full">{pendingCount} pending</span>
              )}
            </h2>
            {!isManager && (
              <button onClick={() => { setError(null); setRequestRows([emptyRequestRow()]); setRequestNotes(''); setActiveCustomerRow(null); setShowRequestModal(true); }}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium">
                <Plus size={14} /> New Request
              </button>
            )}
          </div>
          {loadingRequests ? <Spinner /> : requests.length === 0 ? (
            <Empty msg="No requests yet." />
          ) : (
            <div className="divide-y divide-gray-100">
              {requests.map(req => (
                <HistoryRow key={req.id}
                  title={isManager ? req.requested_by_name : req.item_name}
                  badge={<StatusBadge status={req.status} />}
                  subtitle={isManager
                    ? `${req.item_name} · ${req.quantity} ${req.item_unit}`
                    : `Qty: ${req.quantity} ${req.item_unit}`}
                  meta={new Date(req.requested_at).toLocaleDateString()}
                  expanded={expandedRequest === req.id}
                  onToggle={() => setExpandedRequest(expandedRequest === req.id ? null : req.id)}
                >
                  <div className="pt-3 space-y-2 text-sm">
                    {isManager && (
                      <p className="text-gray-600"><span className="font-medium">Requested by:</span> {req.requested_by_name}</p>
                    )}
                    <p className="text-gray-600"><span className="font-medium">Item:</span> {req.item_name} ({req.item_unit})</p>
                    <p className="text-gray-600"><span className="font-medium">Quantity:</span> {req.quantity}</p>
                    {req.customer_name && (
                      <p className="text-blue-700 bg-blue-50 px-3 py-1.5 rounded-lg text-xs">
                        <span className="font-semibold">Customer:</span> {req.customer_name} ({req.customer_username})
                      </p>
                    )}
                    {req.notes && <p className="text-gray-500 text-xs">Notes: {req.notes}</p>}
                    {req.status === 'rejected' && req.rejection_note && (
                      <p className="text-red-600 bg-red-50 px-3 py-1.5 rounded-lg text-xs">
                        <span className="font-semibold">Rejection reason:</span> {req.rejection_note}
                      </p>
                    )}
                    {req.status === 'issued' && req.reviewed_by_name && (
                      <p className="text-green-700 text-xs">Issued by {req.reviewed_by_name} on {new Date(req.reviewed_at).toLocaleDateString()}</p>
                    )}
                    {isManager && req.status === 'pending' && (
                      <div className="flex gap-2 pt-2">
                        <button
                          onClick={(e) => { e.stopPropagation(); setApprovingRequest(req); setApproveSerial(''); setError(null); }}
                          className="flex items-center gap-1.5 px-3 py-1.5 bg-green-600 text-white text-xs rounded-lg hover:bg-green-700 font-medium">
                          <CheckCircle size={13} /> Approve & Issue
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); setRejectingRequest(req); setRejectNote(''); setError(null); }}
                          className="flex items-center gap-1.5 px-3 py-1.5 bg-red-500 text-white text-xs rounded-lg hover:bg-red-600 font-medium">
                          <XCircle size={13} /> Reject
                        </button>
                      </div>
                    )}
                  </div>
                </HistoryRow>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Monthly Report ── */}
      {tab === 'report' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex flex-wrap items-end gap-4 mb-6">
            <div>
              <label className={lblCls}>Month</label>
              <select value={reportMonth} onChange={e => setReportMonth(Number(e.target.value))} className={fieldCls} style={{ width: 'auto' }}>
                {MONTH_NAMES.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
              </select>
            </div>
            <div>
              <label className={lblCls}>Year</label>
              <input type="number" value={reportYear} onChange={e => setReportYear(Number(e.target.value))}
                className={`${fieldCls} w-24`} min="2020" max="2100" />
            </div>
            <button onClick={() => loadReport(reportYear, reportMonth)} disabled={loadingReport}
              className="flex items-center gap-2 px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm font-medium">
              {loadingReport ? <Loader size={14} className="animate-spin" /> : <BarChart3 size={14} />}
              Refresh
            </button>
            {report.length > 0 && (
              <>
                <button onClick={exportExcel}
                  className="flex items-center gap-2 px-4 py-2 border border-green-600 text-green-700 rounded-lg hover:bg-green-50 text-sm font-medium">
                  <Download size={14} /> Export Excel
                </button>
                <button onClick={exportPDF}
                  className="flex items-center gap-2 px-4 py-2 border border-red-500 text-red-600 rounded-lg hover:bg-red-50 text-sm font-medium">
                  <FileText size={14} /> Export PDF
                </button>
              </>
            )}
          </div>
          {report.length > 0 && (
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Item</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Unit</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase">Purchased</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase">Issued</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase">Returned</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase">Remaining</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {report.map(r => (
                  <tr key={r.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium text-gray-900">{r.name}</td>
                    <td className="px-4 py-3 text-gray-500">{r.unit}</td>
                    <td className="px-4 py-3 text-right">{r.purchased}</td>
                    <td className="px-4 py-3 text-right">{r.issued}</td>
                    <td className="px-4 py-3 text-right">{r.returned}</td>
                    <td className={`px-4 py-3 text-right font-bold ${r.is_low ? 'text-red-600' : 'text-green-700'}`}>{r.remaining}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* ════ Modals ════ */}

      {/* Add Stock Item */}
      {showAddItemModal && (
        <Modal title="Add Stock Item" onClose={() => setShowAddItemModal(false)}>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={lblCls}>Item Name *</label>
                <input type="text" value={newItemForm.name}
                  onChange={e => setNewItemForm(f => ({ ...f, name: e.target.value }))}
                  className={fieldCls} placeholder="e.g. ZTE Modem" />
              </div>
              <div>
                <label className={lblCls}>Unit *</label>
                <input type="text" value={newItemForm.unit}
                  onChange={e => setNewItemForm(f => ({ ...f, unit: e.target.value }))}
                  className={fieldCls} placeholder="pcs / meters / rolls" />
              </div>
            </div>
            <div>
              <label className={lblCls}>Low Stock Alert Threshold</label>
              <input type="number" min="0" value={newItemForm.low_threshold}
                onChange={e => setNewItemForm(f => ({ ...f, low_threshold: Number(e.target.value) }))}
                className={`${fieldCls} w-32`} />
              <p className="text-xs text-gray-400 mt-1">Show red alert when stock drops to or below this number</p>
            </div>
            <div>
              <label className={lblCls}>Notes (optional)</label>
              <input type="text" value={newItemForm.notes}
                onChange={e => setNewItemForm(f => ({ ...f, notes: e.target.value }))}
                className={fieldCls} placeholder="Description, specs..." />
            </div>
            {error && <p className="text-sm text-red-600 flex items-center gap-1"><AlertCircle size={14} />{error}</p>}
            <div className="flex justify-end gap-3 pt-2">
              <button onClick={() => setShowAddItemModal(false)}
                className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
              <button onClick={submitNewItem} disabled={submitting || !newItemForm.name}
                className="px-5 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2">
                {submitting && <Loader size={14} className="animate-spin" />} Save Item
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Edit Stock Item */}
      {editingItem && (
        <Modal title={`Edit: ${editingItem.name}`} onClose={() => { setEditingItem(null); setError(null); }}>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={lblCls}>Item Name *</label>
                <input type="text" value={newItemForm.name}
                  onChange={e => setNewItemForm(f => ({ ...f, name: e.target.value }))}
                  className={fieldCls} />
              </div>
              <div>
                <label className={lblCls}>Unit *</label>
                <input type="text" value={newItemForm.unit}
                  onChange={e => setNewItemForm(f => ({ ...f, unit: e.target.value }))}
                  className={fieldCls} />
              </div>
            </div>
            <div>
              <label className={lblCls}>Low Stock Alert Threshold</label>
              <input type="number" min="0" value={newItemForm.low_threshold}
                onChange={e => setNewItemForm(f => ({ ...f, low_threshold: Number(e.target.value) }))}
                className={`${fieldCls} w-32`} />
            </div>
            <div>
              <label className={lblCls}>Notes</label>
              <input type="text" value={newItemForm.notes}
                onChange={e => setNewItemForm(f => ({ ...f, notes: e.target.value }))}
                className={fieldCls} />
            </div>
            {error && <p className="text-sm text-red-600 flex items-center gap-1"><AlertCircle size={14} />{error}</p>}
            <div className="flex justify-end gap-3 pt-2">
              <button onClick={() => { setEditingItem(null); setError(null); }}
                className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
              <button onClick={submitEditItem} disabled={submitting || !newItemForm.name}
                className="px-5 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2">
                {submitting && <Loader size={14} className="animate-spin" />} Save Changes
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Purchase Modal */}
      {showPurchaseModal && (
        <Modal title="Log Purchase" onClose={() => setShowPurchaseModal(false)}>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={lblCls}>Date *</label>
                <input type="date" value={purchaseForm.date}
                  onChange={e => setPurchaseForm(f => ({ ...f, date: e.target.value }))} className={fieldCls} />
              </div>
              <div>
                <label className={lblCls}>Supplier *</label>
                <input type="text" value={purchaseForm.supplier}
                  onChange={e => setPurchaseForm(f => ({ ...f, supplier: e.target.value }))}
                  className={fieldCls} placeholder="Supplier name" />
              </div>
            </div>
            <div>
              <label className={lblCls}>Notes</label>
              <input type="text" value={purchaseForm.notes}
                onChange={e => setPurchaseForm(f => ({ ...f, notes: e.target.value }))}
                className={fieldCls} placeholder="Invoice #, notes..." />
            </div>
            <div>
              <label className={lblCls}>Items *</label>
              <LineItemRows
                rows={purchaseForm.rows}
                stockItems={stockItems}
                showPrice
                onUpdate={pRow}
                onAdd={() => setPurchaseForm(f => ({ ...f, rows: [...f.rows, emptyPurchaseRow()] }))}
                onRemove={i => setPurchaseForm(f => ({ ...f, rows: f.rows.filter((_, idx) => idx !== i) }))}
              />
            </div>
            {error && <p className="text-sm text-red-600 flex items-center gap-1"><AlertCircle size={14} />{error}</p>}
            <div className="flex justify-end gap-3 pt-2">
              <button onClick={() => setShowPurchaseModal(false)}
                className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
              <button onClick={submitPurchase} disabled={submitting}
                className="px-5 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2">
                {submitting && <Loader size={14} className="animate-spin" />} Save Purchase
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Issuance Modal */}
      {showIssuanceModal && (
        <Modal title="Issue Items to Worker" onClose={() => setShowIssuanceModal(false)}>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={lblCls}>Date *</label>
                <input type="date" value={issuanceForm.date}
                  onChange={e => setIssuanceForm(f => ({ ...f, date: e.target.value }))} className={fieldCls} />
              </div>
              <div>
                <label className={lblCls}>Employee *</label>
                <select value={issuanceForm.issued_to}
                  onChange={e => setIssuanceForm(f => ({ ...f, issued_to: e.target.value }))} className={fieldCls}>
                  <option value="">— Select employee —</option>
                  {employees.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
                </select>
              </div>
            </div>
            <CustomerSearchField form={issuanceForm} setForm={setIssuanceForm} customerResults={customerResults} setCustomerResults={setCustomerResults} searchingCustomers={searchingCustomers} onSearch={searchCustomers} />
            <div>
              <label className={lblCls}>Notes</label>
              <input type="text" value={issuanceForm.notes}
                onChange={e => setIssuanceForm(f => ({ ...f, notes: e.target.value }))}
                className={fieldCls} placeholder="Task, job reference..." />
            </div>
            <div>
              <label className={lblCls}>Items * <span className="text-gray-400 font-normal">(enter MAC/Serial for modems)</span></label>
              <LineItemRows
                rows={issuanceForm.rows}
                stockItems={stockItems}
                showMac
                onUpdate={iRow}
                onAdd={() => setIssuanceForm(f => ({ ...f, rows: [...f.rows, emptyIssuanceRow()] }))}
                onRemove={i => setIssuanceForm(f => ({ ...f, rows: f.rows.filter((_, idx) => idx !== i) }))}
              />
            </div>
            {error && <p className="text-sm text-red-600 flex items-center gap-1"><AlertCircle size={14} />{error}</p>}
            <div className="flex justify-end gap-3 pt-2">
              <button onClick={() => setShowIssuanceModal(false)}
                className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
              <button onClick={submitIssuance} disabled={submitting}
                className="px-5 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-2">
                {submitting && <Loader size={14} className="animate-spin" />} Issue Items
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Return Modal */}
      {showReturnModal && (
        <Modal title="Log Return" onClose={() => setShowReturnModal(false)}>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={lblCls}>Date *</label>
                <input type="date" value={returnForm.date}
                  onChange={e => setReturnForm(f => ({ ...f, date: e.target.value }))} className={fieldCls} />
              </div>
              <div>
                <label className={lblCls}>Returned By *</label>
                <select value={returnForm.returned_by}
                  onChange={e => setReturnForm(f => ({ ...f, returned_by: e.target.value }))} className={fieldCls}>
                  <option value="">— Select employee —</option>
                  {employees.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label className={lblCls}>Notes</label>
              <input type="text" value={returnForm.notes}
                onChange={e => setReturnForm(f => ({ ...f, notes: e.target.value }))}
                className={fieldCls} placeholder="Reason for return..." />
            </div>
            <div>
              <label className={lblCls}>Items *</label>
              <LineItemRows
                rows={returnForm.rows}
                stockItems={stockItems}
                onUpdate={rRow}
                onAdd={() => setReturnForm(f => ({ ...f, rows: [...f.rows, emptyReturnRow()] }))}
                onRemove={i => setReturnForm(f => ({ ...f, rows: f.rows.filter((_, idx) => idx !== i) }))}
              />
            </div>
            {error && <p className="text-sm text-red-600 flex items-center gap-1"><AlertCircle size={14} />{error}</p>}
            <div className="flex justify-end gap-3 pt-2">
              <button onClick={() => setShowReturnModal(false)}
                className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
              <button onClick={submitReturn} disabled={submitting}
                className="px-5 py-2 bg-orange-500 text-white text-sm rounded-lg hover:bg-orange-600 disabled:opacity-50 flex items-center gap-2">
                {submitting && <Loader size={14} className="animate-spin" />} Log Return
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Request Item Modal (employees) */}
      {showRequestModal && (
        <Modal title="Request Stock Items" onClose={() => { setShowRequestModal(false); setCustomerResults([]); setCustomerSearchEmpty(false); setActiveCustomerRow(null); }}>
          <div className="space-y-4">
            <p className="text-xs text-gray-500 bg-blue-50 px-3 py-2 rounded-lg">
              You can request multiple items at once. For modems, link each one to a customer using the customer field on each row.
            </p>

            {/* Per-row items */}
            <div className="space-y-3">
              {requestRows.map((row, i) => (
                <div key={i} className="border border-gray-200 rounded-lg p-3 space-y-3 relative">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Item {i + 1}</span>
                    {requestRows.length > 1 && (
                      <button
                        onClick={() => {
                          setRequestRows(r => r.filter((_, idx) => idx !== i));
                          if (activeCustomerRow === i) { setActiveCustomerRow(null); setCustomerResults([]); setCustomerSearchEmpty(false); }
                        }}
                        className="p-1 text-red-400 hover:bg-red-50 rounded flex items-center gap-1 text-xs"
                      ><Trash2 size={12} /> Remove</button>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className={lblCls}>Item *</label>
                      <select
                        value={row.item}
                        onChange={e => setRequestRows(r => r.map((x, idx) => idx === i ? { ...x, item: e.target.value } : x))}
                        className={fieldCls}
                      >
                        <option value="">— Select item —</option>
                        {inventory.filter(inv => inv.current_stock > 0).map(inv => (
                          <option key={inv.id} value={inv.id}>{inv.name} ({inv.unit}) — {inv.current_stock} avail.</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className={lblCls}>Quantity *</label>
                      <input type="number" min="1" value={row.quantity}
                        onChange={e => setRequestRows(r => r.map((x, idx) => idx === i ? { ...x, quantity: e.target.value } : x))}
                        className={fieldCls} />
                    </div>
                  </div>
                  {/* Per-row customer search */}
                  <div className="relative">
                    <label className={lblCls}>Link to Customer (optional — for modem/warranty tracking)</label>
                    {row.customer ? (
                      <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg text-sm">
                        <span className="flex-1 text-blue-800 font-medium">{row.customerSearch}</span>
                        <button
                          onClick={() => setRequestRows(r => r.map((x, idx) => idx === i ? { ...x, customer: '', customerSearch: '' } : x))}
                          className="text-blue-400 hover:text-red-500"
                        ><X size={14} /></button>
                      </div>
                    ) : (
                      <>
                        <div className="relative">
                          <input
                            type="text"
                            value={row.customerSearch}
                            onChange={e => {
                              const v = e.target.value;
                              setRequestRows(r => r.map((x, idx) => idx === i ? { ...x, customerSearch: v, customer: '' } : x));
                              setActiveCustomerRow(i);
                              searchCustomers(v);
                            }}
                            onFocus={() => {
                              setActiveCustomerRow(i);
                              if (row.customerSearch.length >= 2) searchCustomers(row.customerSearch);
                            }}
                            onBlur={() => { setTimeout(() => { setActiveCustomerRow(null); setCustomerSearchEmpty(false); }, 150); }}
                            className={fieldCls}
                            placeholder="Type username, name or phone..."
                            autoComplete="off"
                          />
                          {searchingCustomers && activeCustomerRow === i && (
                            <Loader size={14} className="animate-spin absolute right-3 top-2.5 text-gray-400" />
                          )}
                        </div>
                        {activeCustomerRow === i && customerResults.length > 0 && (
                          <div className="absolute z-10 mt-1 w-full border border-gray-200 rounded-lg bg-white shadow-lg max-h-48 overflow-y-auto">
                            {customerResults.map(c => (
                              <button key={c.id} className="w-full text-left px-3 py-2.5 text-sm hover:bg-blue-50 flex items-center justify-between gap-2"
                                onMouseDown={e => e.preventDefault()}
                                onClick={() => {
                                  setRequestRows(r => r.map((x, idx) => idx === i ? { ...x, customer: c.id, customerSearch: `${c.name} (${c.username})` } : x));
                                  setCustomerResults([]);
                                  setCustomerSearchEmpty(false);
                                  setActiveCustomerRow(null);
                                }}>
                                <div>
                                  <span className="font-medium text-gray-900">{c.name}</span>
                                  <span className="text-xs text-gray-400 ml-2">{c.username}</span>
                                  {c.phone && <span className="text-xs text-gray-300 ml-1">{c.phone}</span>}
                                </div>
                                <span className="text-xs text-gray-300 flex-shrink-0">#{c.id}</span>
                              </button>
                            ))}
                          </div>
                        )}
                        {activeCustomerRow === i && customerSearchEmpty && !searchingCustomers && row.customerSearch.length >= 2 && (
                          <div className="absolute z-10 mt-1 w-full border border-orange-100 rounded-lg bg-orange-50 shadow px-3 py-2.5 text-xs text-orange-700">
                            No customers found in your area for "{row.customerSearch}"
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <button
              onClick={() => setRequestRows(r => [...r, emptyRequestRow()])}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 border-2 border-dashed border-blue-300 text-blue-600 rounded-lg hover:bg-blue-50 text-sm font-medium transition-colors"
            >
              <Plus size={15} /> Add Another Item
            </button>

            <div>
              <label className={lblCls}>Notes (optional)</label>
              <input type="text" value={requestNotes}
                onChange={e => setRequestNotes(e.target.value)}
                className={fieldCls} placeholder="Purpose, job reference..." />
            </div>

            {error && <p className="text-sm text-red-600 flex items-center gap-1"><AlertCircle size={14} />{error}</p>}
            <div className="flex justify-end gap-3 pt-2">
              <button onClick={() => { setShowRequestModal(false); setCustomerResults([]); setCustomerSearchEmpty(false); setActiveCustomerRow(null); }}
                className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
              <button onClick={submitRequest} disabled={submitting || requestRows.every(r => !r.item)}
                className="px-5 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2">
                {submitting && <Loader size={14} className="animate-spin" />}
                Submit Request{requestRows.filter(r => r.item).length > 1 ? `s (${requestRows.filter(r => r.item).length})` : ''}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Approve Modal */}
      {approvingRequest && (
        <Modal title="Approve & Issue" onClose={() => setApprovingRequest(null)}>
          <div className="space-y-4">
            <div className="bg-green-50 border border-green-100 rounded-lg px-4 py-3 text-sm text-green-800">
              <p className="font-semibold mb-1">Request from {approvingRequest.requested_by_name}</p>
              <p>{approvingRequest.quantity}× {approvingRequest.item_name}</p>
              {approvingRequest.customer_name && (
                <p className="text-xs mt-1 text-green-600">Customer: {approvingRequest.customer_name} ({approvingRequest.customer_username})</p>
              )}
            </div>
            <div>
              <label className={lblCls}>MAC / Serial Number (optional)</label>
              <input type="text" value={approveSerial}
                onChange={e => setApproveSerial(e.target.value)}
                className={fieldCls} placeholder="e.g. AA:BB:CC:DD:EE:FF" />
              <p className="text-xs text-gray-400 mt-1">Fill this in for modems or serialized items</p>
            </div>
            {error && <p className="text-sm text-red-600 flex items-center gap-1"><AlertCircle size={14} />{error}</p>}
            <div className="flex justify-end gap-3 pt-2">
              <button onClick={() => setApprovingRequest(null)}
                className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
              <button onClick={approveRequest} disabled={submitting}
                className="px-5 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-2">
                {submitting && <Loader size={14} className="animate-spin" />}
                <CheckCircle size={14} /> Confirm Issue
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Reject Modal */}
      {rejectingRequest && (
        <Modal title="Reject Request" onClose={() => setRejectingRequest(null)}>
          <div className="space-y-4">
            <div className="bg-red-50 border border-red-100 rounded-lg px-4 py-3 text-sm text-red-800">
              <p className="font-semibold">{rejectingRequest.requested_by_name} — {rejectingRequest.quantity}× {rejectingRequest.item_name}</p>
            </div>
            <div>
              <label className={lblCls}>Reason for rejection (optional)</label>
              <input type="text" value={rejectNote}
                onChange={e => setRejectNote(e.target.value)}
                className={fieldCls} placeholder="Out of stock, not approved, etc." />
            </div>
            {error && <p className="text-sm text-red-600 flex items-center gap-1"><AlertCircle size={14} />{error}</p>}
            <div className="flex justify-end gap-3 pt-2">
              <button onClick={() => setRejectingRequest(null)}
                className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
              <button onClick={rejectRequest} disabled={submitting}
                className="px-5 py-2 bg-red-500 text-white text-sm rounded-lg hover:bg-red-600 disabled:opacity-50 flex items-center gap-2">
                {submitting && <Loader size={14} className="animate-spin" />}
                <XCircle size={14} /> Confirm Reject
              </button>
            </div>
          </div>
        </Modal>
      )}

    </div>
  );
};

export default Stock;
